import React, { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { Coins, TrendingUp, Package, Users, Sparkles } from 'lucide-react';
import { blinksApi } from '../services/blinksApi';

interface CreatorEarning {
  crateId: string;
  crateName: string;
  totalVolume: number;
  creatorEarnings: number;
  totalPlays: number;
  entryCost: number;
  createdAt: Date;
}

interface AffiliateEarning {
  crateId: string;
  crateName: string;
  totalEarnings: number;
  totalPlays: number;
  firstPlayAt: Date;
  lastPlayAt: Date;
}

interface RewardsData {
  wallet: string;
  creator: {
    totalEarnings: number;
    cases: CreatorEarning[];
  };
  affiliate: {
    totalEarnings: number;
    cases: AffiliateEarning[];
  };
  totalEarnings: number;
}

const Rewards: React.FC = () => {
  const { publicKey, connected } = useWallet();
  const [rewards, setRewards] = useState<RewardsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadRewards = async () => {
      if (!connected || !publicKey) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const data = await blinksApi.getRewards(publicKey.toString());
        setRewards(data);
      } catch (err: any) {
        console.error('Error loading rewards:', err);
        setError(err.message || 'Failed to load rewards');
      } finally {
        setIsLoading(false);
      }
    };

    loadRewards();
  }, [publicKey, connected]);

  const formatTokenAmount = (amount: number): string => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(2)}M`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(2)}K`;
    }
    return amount.toFixed(2);
  };

  const formatDate = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (!connected || !publicKey) {
    return (
      <div className="rewards-container">
        <div className="section-header">
          <h3>Rewards</h3>
          <p className="section-desc">Connect your wallet to view your creator and affiliate earnings.</p>
        </div>
        <div className="empty-state">
          <Sparkles size={48} color="#888" />
          <p>Please connect your wallet to view rewards</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="rewards-container">
        <div className="section-header">
          <h3>Rewards</h3>
          <p className="section-desc">View your creator and affiliate earnings across all cases.</p>
        </div>
        <div className="empty-state">
          <Sparkles size={48} color="#888" />
          <p>Loading rewards...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rewards-container">
        <div className="section-header">
          <h3>Rewards</h3>
          <p className="section-desc">View your creator and affiliate earnings across all cases.</p>
        </div>
        <div className="empty-state">
          <p style={{ color: '#FF4444' }}>Error: {error}</p>
        </div>
      </div>
    );
  }

  if (!rewards) {
    return (
      <div className="rewards-container">
        <div className="section-header">
          <h3>Rewards</h3>
          <p className="section-desc">View your creator and affiliate earnings across all cases.</p>
        </div>
        <div className="empty-state">
          <Sparkles size={48} color="#888" />
          <p>No rewards data found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rewards-container">
      <div className="section-header">
        <h3>Rewards</h3>
        <p className="section-desc">View your creator and affiliate earnings across all cases.</p>
      </div>

      {/* Total Earnings Summary */}
      <div className="rewards-summary">
        <div className="summary-card">
          <div className="summary-icon">
            <Coins size={24} />
          </div>
          <div className="summary-content">
            <div className="summary-label">Total Earnings</div>
            <div className="summary-value">{formatTokenAmount(rewards.totalEarnings)} $BLOOT</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">
            <Package size={24} />
          </div>
          <div className="summary-content">
            <div className="summary-label">Creator Earnings</div>
            <div className="summary-value">{formatTokenAmount(rewards.creator.totalEarnings)} $BLOOT</div>
            <div className="summary-subtext">{rewards.creator.cases.length} case{rewards.creator.cases.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">
            <TrendingUp size={24} />
          </div>
          <div className="summary-content">
            <div className="summary-label">Affiliate Earnings</div>
            <div className="summary-value">{formatTokenAmount(rewards.affiliate.totalEarnings)} $BLOOT</div>
            <div className="summary-subtext">{rewards.affiliate.cases.length} case{rewards.affiliate.cases.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
      </div>

      {/* Creator Earnings Section */}
      {rewards.creator.cases.length > 0 && (
        <div className="rewards-section">
          <h4 className="rewards-section-title">
            <Package size={20} />
            Creator Earnings
          </h4>
          <p className="rewards-section-desc">Earnings from cases you created (1% of total volume)</p>
          <div className="rewards-table">
            <div className="rewards-table-header">
              <div className="table-col-name">Case Name</div>
              <div className="table-col-plays">Plays</div>
              <div className="table-col-volume">Total Volume</div>
              <div className="table-col-earnings">Your Earnings</div>
            </div>
            {rewards.creator.cases.map((earning) => (
              <div key={earning.crateId} className="rewards-table-row">
                <div className="table-col-name">
                  <div className="case-name">{earning.crateName}</div>
                </div>
                <div className="table-col-plays">
                  <Users size={14} style={{ marginRight: '4px', opacity: 0.7 }} />
                  {earning.totalPlays.toLocaleString()}
                </div>
                <div className="table-col-volume">
                  {formatTokenAmount(earning.totalVolume)} $BLOOT
                </div>
                <div className="table-col-earnings">
                  <span className="earnings-amount">{formatTokenAmount(earning.creatorEarnings)} $BLOOT</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Affiliate Earnings Section */}
      {rewards.affiliate.cases.length > 0 && (
        <div className="rewards-section">
          <h4 className="rewards-section-title">
            <TrendingUp size={20} />
            Affiliate Earnings
          </h4>
          <p className="rewards-section-desc">Earnings from referring players (1% of entry cost per play)</p>
          <div className="rewards-table">
            <div className="rewards-table-header">
              <div className="table-col-name">Case Name</div>
              <div className="table-col-plays">Plays</div>
              <div className="table-col-earnings">Your Earnings</div>
              <div className="table-col-date">Last Play</div>
            </div>
            {rewards.affiliate.cases.map((earning) => (
              <div key={earning.crateId} className="rewards-table-row">
                <div className="table-col-name">
                  <div className="case-name">{earning.crateName}</div>
                </div>
                <div className="table-col-plays">
                  <Users size={14} style={{ marginRight: '4px', opacity: 0.7 }} />
                  {earning.totalPlays.toLocaleString()}
                </div>
                <div className="table-col-earnings">
                  <span className="earnings-amount">{formatTokenAmount(earning.totalEarnings)} $BLOOT</span>
                </div>
                <div className="table-col-date">
                  {formatDate(earning.lastPlayAt)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty States */}
      {rewards.creator.cases.length === 0 && rewards.affiliate.cases.length === 0 && (
        <div className="empty-state">
          <Sparkles size={48} color="#888" />
          <p>No earnings yet</p>
          <p className="empty-hint">Create cases or share affiliate links to start earning!</p>
        </div>
      )}
    </div>
  );
};

export default Rewards;

